import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Bell, CheckCircle, AlertTriangle, Info, Clock, Check, Loader2, Shield, Zap, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { API } from '../../config/apiBase';


export default function AlertsPage() {
    const { user } = useAuth();
    const [alertas, setAlertas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const isEmpresa = user?.account_type === 'empresa' || user?.account_type === 'client';

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };

    useEffect(() => {
        fetch(`${API}/alertas`, { headers }).then(r => r.json())
            .then(data => { setAlertas(Array.isArray(data) ? data : []); setLoading(false); })
            .catch(() => setLoading(false));
    }, []);

    const markRead = async (id) => {
        await fetch(`${API}/alertas/read/${id}`, { method: 'POST', headers });
        setAlertas(prev => prev.map(a => a.alerta_id === id ? { ...a, read_by: [...(a.read_by || []), user?.user_id] } : a));
    };

    const markAllRead = async () => {
        await fetch(`${API}/alertas/read-all`, { method: 'POST', headers });
        setAlertas(prev => prev.map(a => ({ ...a, read_by: [...(a.read_by || []), user?.user_id] })));
    };

    const isRead = (a) => (a.read_by || []).includes(user?.user_id);
    const unreadCount = alertas.filter(a => !isRead(a)).length;

    const categories = isEmpresa
        ? ['all', 'operacional', 'ponto', 'administrativo', 'sistema']
        : ['all', 'pessoal', 'ponto', 'empresa', 'sistema'];
    const catLabels = { all: 'Todos', operacional: 'Operacional', ponto: 'Ponto', administrativo: 'Admin', sistema: 'Sistema', pessoal: 'Pessoal', empresa: 'Empresa' };

    const filtered = filter === 'all' ? alertas : alertas.filter(a => a.categoria === filter);
    const priorityIcons = { alta: AlertTriangle, media: Info, baixa: CheckCircle };
    const priorityColors = { alta: '#EF4444', media: '#F59E0B', baixa: '#10B981' };

    if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--sv-gold-primary)' }} /></div>;

    return (
        <div className="space-y-5" data-testid="alertas-page">
            {/* Summary KPIs */}
            <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl p-3.5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <p className="text-xl font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>{alertas.length}</p>
                    <p className="text-[10px]" style={{ color: 'var(--muted)' }}>Total de alertas</p>
                </div>
                <div className="rounded-xl p-3.5" style={{ background: unreadCount > 0 ? 'rgba(239,68,68,0.04)' : 'var(--surface)', border: `1px solid ${unreadCount > 0 ? 'rgba(239,68,68,0.15)' : 'var(--border)'}` }}>
                    <p className="text-xl font-bold" style={{ color: unreadCount > 0 ? '#EF4444' : 'var(--text)', fontFamily: "var(--font-display)" }}>{unreadCount}</p>
                    <p className="text-[10px]" style={{ color: 'var(--muted)' }}>Não lidos</p>
                </div>
                <div className="rounded-xl p-3.5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <p className="text-xl font-bold" style={{ color: '#10B981', fontFamily: "var(--font-display)" }}>{alertas.filter(a => isRead(a)).length}</p>
                    <p className="text-[10px]" style={{ color: 'var(--muted)' }}>Lidos</p>
                </div>
            </div>

            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    {unreadCount > 0 && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ background: 'rgba(239,68,68,0.12)', color: '#EF4444' }}>
                            {unreadCount} não lido(s)
                        </span>
                    )}
                </div>
                {unreadCount > 0 && (
                    <Button onClick={markAllRead} variant="outline" className="text-xs h-8" data-testid="mark-all-read">
                        <Check size={12} className="mr-1" /> Marcar todos como lidos
                    </Button>
                )}
            </div>

            {/* Filters */}
            <div className="flex gap-1.5 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
                {categories.map((c) => (
                    <button key={c} onClick={() => setFilter(c)}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all"
                        style={{ background: filter === c ? 'rgba(var(--sv-accent-rgb), 0.15)' : 'var(--surface)', border: `1px solid ${filter === c ? 'rgba(var(--sv-accent-rgb), 0.3)' : 'var(--border)'}`, color: filter === c ? 'var(--sv-accent)' : 'var(--muted)' }}
                        data-testid={`filter-${c}`}>
                        {catLabels[c] || c}
                    </button>
                ))}
            </div>

            {/* List */}
            {filtered.length === 0 ? (
                <div className="text-center py-16 rounded-xl" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(var(--sv-accent-rgb), 0.06)' }}>
                        <Bell size={28} style={{ color: 'var(--muted)', opacity: 0.5 }} />
                    </div>
                    <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>Nenhum alerta encontrado</p>
                    <p className="text-xs mt-1 max-w-xs mx-auto" style={{ color: 'var(--muted)' }}>
                        Você será notificado automaticamente sobre eventos importantes como ponto irregular, vencimento de plano e novas solicitações.
                    </p>
                    <div className="flex items-center justify-center gap-4 mt-5">
                        <div className="flex items-center gap-1.5 text-[10px]" style={{ color: 'var(--muted)' }}>
                            <div className="w-2 h-2 rounded-full" style={{ background: '#EF4444' }} /> Alta prioridade
                        </div>
                        <div className="flex items-center gap-1.5 text-[10px]" style={{ color: 'var(--muted)' }}>
                            <div className="w-2 h-2 rounded-full" style={{ background: '#F59E0B' }} /> Média
                        </div>
                        <div className="flex items-center gap-1.5 text-[10px]" style={{ color: 'var(--muted)' }}>
                            <div className="w-2 h-2 rounded-full" style={{ background: '#10B981' }} /> Baixa
                        </div>
                    </div>
                </div>
            ) : (
                <div className="space-y-2">
                    {filtered.map((a) => {
                        const read = isRead(a);
                        const Icon = priorityIcons[a.prioridade] || Info;
                        const color = priorityColors[a.prioridade] || '#6B7280';
                        return (
                            <div key={a.alerta_id} className="rounded-xl p-4 transition-all" data-testid={`alerta-${a.alerta_id}`}
                                style={{ background: read ? 'var(--surface)' : `${color}06`, border: `1px solid ${read ? 'var(--border)' : `${color}20`}`, opacity: read ? 0.7 : 1 }}>
                                <div className="flex items-start gap-3">
                                    <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5" style={{ background: `${color}12` }}>
                                        <Icon size={15} style={{ color }} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>{a.titulo || a.message || 'Alerta'}</p>
                                            {!read && <div className="w-2 h-2 rounded-full shrink-0 animate-pulse" style={{ background: color }} />}
                                        </div>
                                        {a.descricao && <p className="text-xs mt-0.5" style={{ color: 'var(--muted)' }}>{a.descricao}</p>}
                                        <div className="flex items-center gap-3 mt-2">
                                            <span className="text-[10px] flex items-center gap-1" style={{ color: 'var(--muted)' }}>
                                                <Clock size={9} />
                                                {a.created_at ? new Date(a.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                                            </span>
                                            <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: `${color}10`, color }}>{a.prioridade || 'media'}</span>
                                            {a.categoria && <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'var(--surface-2)', color: 'var(--muted)', border: '1px solid var(--border)' }}>{catLabels[a.categoria] || a.categoria}</span>}
                                            {!read && (
                                                <button onClick={() => markRead(a.alerta_id)} className="text-[10px] font-medium underline" style={{ color: 'var(--sv-gold-primary)' }} data-testid={`mark-read-${a.alerta_id}`}>
                                                    Marcar como lido
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Upgrade CTA */}
            <div className="rounded-xl p-4" style={{ background: 'rgba(var(--sv-accent-rgb), 0.04)', border: '1px solid rgba(var(--sv-accent-rgb), 0.1)' }}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Zap size={14} style={{ color: 'var(--sv-gold-primary)' }} />
                        <div>
                            <p className="text-xs font-semibold" style={{ color: 'var(--sv-gold-primary)' }}>Alertas inteligentes</p>
                            <p className="text-[10px]" style={{ color: 'var(--muted)' }}>Receba alertas por email e Telegram com planos superiores.</p>
                        </div>
                    </div>
                    <Link to="/planos" className="text-[10px] font-medium px-3 py-1.5 rounded-lg transition-all hover:scale-105" style={{ background: 'rgba(var(--sv-accent-rgb), 0.15)', color: 'var(--sv-gold-primary)' }} data-testid="upgrade-alertas">
                        Ver planos <ArrowRight size={10} className="inline ml-0.5" />
                    </Link>
                </div>
            </div>
        </div>
    );
}
